import { Stack } from '@mui/material';
import { FlowHumanTaskType } from 'flow-schema';

import { isTruthy } from 'common';

import { ClinicalQuestionAssistantDefaultModelField } from './DefaultModelField';
import { ClinicalQuestionAssistantDefaultPromptField } from './DefaultPromptField';
import {
  CLINICAL_QUESTION_ASSISTANT_DEFAULT_OUTPUT_REQUIREMENTS,
  CLINICAL_QUESTION_ASSISTANT_DEFAULT_PREAMBLE,
} from './defaults';
import {
  BlockValueTemplateField,
  BlockValueTemplateFieldArea,
} from '../../../../ConfigFields/CodeMirror/FlowTemplate';
import { useSelectedNodeField } from '../../../../ConfigFields/useSelectedNodeField';

interface ClinicalQuestionAssistantConfigProps {
  taskType: FlowHumanTaskType.CustomSubtask | FlowHumanTaskType.SubmitPriorAuthorization;
}

/**
 * Shared Clinical Question Assistant settings backed by the appropriate Custom or Prior Authorization field paths.
 */
export function ClinicalQuestionAssistantConfig(props: ClinicalQuestionAssistantConfigProps) {
  const { taskType } = props;
  const isPriorAuthorization = taskType === FlowHumanTaskType.SubmitPriorAuthorization;
  const { value: legacyCompanionDocuments } = useSelectedNodeField<string | undefined>(
    'config.priorAuthorizationInformation.copilotDataContext'
  );

  return (
    <Stack gap={2} sx={{ marginLeft: 6 }}>
      {isPriorAuthorization && isTruthy(legacyCompanionDocuments) && (
        <BlockValueTemplateField
          fieldName="config.priorAuthorizationInformation.copilotDataContext"
          label="[LEGACY] Companion Documents"
          helperText="Deprecated. This field remains available only because this flow already uses it. Prefer Data Context for new configuration."
        />
      )}
      <BlockValueTemplateField
        fieldName={
          isPriorAuthorization
            ? 'config.priorAuthorizationInformation.copilotDataContextObject'
            : 'config.tools.config.clinicalQuestionAssistant.dataContext'
        }
        label="Data Context"
        helperText="Enter a flow template that resolves to a JSON object containing the clinical context available to the assistant, such as documents and lab values."
      />
      <ClinicalQuestionAssistantDefaultPromptField
        fieldName={
          isPriorAuthorization
            ? 'config.priorAuthorizationInformation.predictionOptions.preamble'
            : 'config.tools.config.clinicalQuestionAssistant.preamble'
        }
        label="Preamble"
        helperText="Define the assistant's role and task. The displayed value is used by default and is saved only after you edit it."
        defaultValue={CLINICAL_QUESTION_ASSISTANT_DEFAULT_PREAMBLE}
      />
      <ClinicalQuestionAssistantDefaultPromptField
        fieldName={
          isPriorAuthorization
            ? 'config.priorAuthorizationInformation.predictionOptions.outputRequirement'
            : 'config.tools.config.clinicalQuestionAssistant.outputRequirements'
        }
        label="Output Requirements"
        helperText="Define how the assistant should structure its output. The displayed value is used by default and is saved only after you edit it."
        defaultValue={CLINICAL_QUESTION_ASSISTANT_DEFAULT_OUTPUT_REQUIREMENTS}
      />
      <BlockValueTemplateFieldArea
        fieldName={
          isPriorAuthorization
            ? 'config.priorAuthorizationInformation.predictionOptions.outro'
            : 'config.tools.config.clinicalQuestionAssistant.additionalInstructions'
        }
        label="Additional Instructions"
        helperText="Optional instructions appended to the prompt, such as guidance from a reference table for the ordered procedure."
      />
      <ClinicalQuestionAssistantDefaultModelField
        fieldName={
          isPriorAuthorization
            ? 'config.priorAuthorizationInformation.modelName'
            : 'config.tools.config.clinicalQuestionAssistant.modelName'
        }
      />
    </Stack>
  );
}
